import { User } from '@/services/userService/userTypes';
import * as Sentry from '@sentry/nextjs';
import { AxiosError } from 'axios';
import type {
  ConfirmCardSetupData,
  ConfirmCardSetupOptions,
  Stripe,
  StripeElements,
} from '@stripe/stripe-js';
import { SWRResponse } from 'swr';
import { HttpService } from '@/core/http/httpService';
import { serviceSingletonGetter } from '@/services/serviceGetter';
import { Interval } from '../priceService';
import { PaymentStrategies, RecurringPayments } from '../settingsService';
import formatRecurringInterval from './formatRecurringInterval';
import { STRIPE_ACCOUNT_ID } from './paymentConstants';
import {
  Intents,
  PaymentPlatform,
  PaymentProvider,
  PaymentStrategiesIds,
  PortalSession,
  Subscriptions,
  SubscriptionInfo,
  UpgradePreviewInfo,
  WithdrawalEligibility,
  WithdrawalResult,
} from './paymentTypes';
import { useWithdrawalEligibility } from './useWithdrawalEligibility';

export type ConfirmCardSetupCallback = (
  clientSecret: string,
  paymentConfig: ConfirmCardSetupData,
  handleActions: ConfirmCardSetupOptions
) => Promise<{
  paymentMethodId: string;
}>;

export type PaymentService = {
  createPortalSession: (
    cancellationCode: string,
    customerId: string
  ) => Promise<PortalSession>;
  getStripeClientIntentsData: () => Promise<Intents>;
  createStripeSubscription: (
    paymentMethodId: string,
    priceId: string,
    isFreeTrialEnabled: boolean,
    introPriceId?: string,
    unlockedExports?: number | null
  ) => Promise<Subscriptions>;
  createPaypalSubscription: (
    subscriptionId: string,
    unlockedExports?: number | null
  ) => Promise<any>;
  getSubscription: (subscriptionProvider: string) => Promise<SubscriptionInfo>;
  previewSubscriptionUpgrade: (
    subscriptionProvider: string,
    newPriceId?: string
  ) => Promise<UpgradePreviewInfo>;
  upgradeStripeSubscription: (newPriceId: string) => Promise<void>;
  upgradePaypalSubscription: (newSubscriptionId: string) => Promise<void>;
  cancelSubscription: (subscriptionProvider: string) => Promise<any>;
  cancelSubscriptionFromEmail: (
    subscriptionProvider: string,
    cancellationCode: string
  ) => Promise<any>;
  getWithdrawalEligibility: (
    subscriptionProvider: string
  ) => Promise<WithdrawalEligibility>;
  exerciseWithdrawal: (
    subscriptionProvider: string,
    confirmationEmail: string
  ) => Promise<WithdrawalResult>;
  isPaypal: (providerRef: string) => boolean;
  trackPayment: (event: {
    track: (
      category: string,
      action: string,
      additionalParams?: Record<string, any> | undefined,
      label?: string | undefined
    ) => void;
    status?: string;
    paymentPlatform?: PaymentPlatform;
    priceId?: string;
    freeTrialEnabled?: boolean;
    emailReminderEnabled?: boolean;
    paymentStrategy: 'subscription';
    webOnboardingUserId?: string;
    webOnboardingLocalStorageId?: string;
    paywallVersion?: string;
    previousAction?: string;
    offerPlan?: 'weekly' | 'yearly' | 'welcome' | 'free';
  }) => void;
  catchStripePriceError: (
    stripe?: Stripe | null,
    elements?: StripeElements | null,
    priceId?: string,
    ev?: any
  ) => any;
  isPremiumUser: (user?: User) => boolean;
  isBaseUser: (user?: User) => boolean;
  getPaymentStrategiesWithoutBase: (
    paymentStrategies: PaymentStrategies
  ) => [PaymentStrategiesIds, RecurringPayments][];
  getUserPlanFromPriceId: (
    settingsPaymentStrategies: PaymentStrategies,
    userPriceId?: User['monetization']['priceId']
  ) => PaymentStrategiesIds | '';
  isBusinessUser: (user?: User) => boolean;
  isPersonalUser: (user?: User) => boolean;
  formatRecurringInterval: (
    interval: Interval,
    intervalCount: number
  ) => string;
  useWithdrawalEligibility: () => SWRResponse<
    WithdrawalEligibility,
    AxiosError<unknown, any>
  >;
};

export const createPaymentService = ({
  post,
  get,
  put,
  deleteRequest,
}: HttpService): PaymentService => {
  const createPortalSession = async (
    cancellationCode: string,
    customerId: string
  ): Promise<PortalSession> =>
    post('/v1/web/stripe/portals-migration', {
      cancellationCode,
      customerId,
    });

  const getStripeClientIntentsData = async (): Promise<Intents> =>
    post('/v1/web/stripe/intents', {
      stripe_account: STRIPE_ACCOUNT_ID,
    });

  const createStripeSubscription = async (
    paymentMethodId: string,
    priceId: string,
    isFreeTrialEnabled: boolean,
    introPriceId?: string,
    unlockedExports?: number | null
  ): Promise<Subscriptions> =>
    post('/v1/web/stripe/subscriptions', {
      paymentMethodId,
      priceId,
      trial: isFreeTrialEnabled,
      unlockedExports,
      introPriceId: introPriceId || null,
    });

  const createPaypalSubscription = async (
    subscriptionId: string,
    unlockedExports?: number | null
  ) =>
    post('/v1/web/paypal/subscriptions', {
      subscriptionId,
      unlockedExports,
    });

  const getSubscription = async (
    subscriptionProvider: string
  ): Promise<SubscriptionInfo> =>
    get(`/v1/web/${subscriptionProvider}/subscriptions`);

  const previewSubscriptionUpgrade = async (
    subscriptionProvider: string,
    newPriceId?: string
  ): Promise<UpgradePreviewInfo> => {
    let requestPath = `/v1/web/${subscriptionProvider}/subscriptions/upgrade/preview`;
    if (subscriptionProvider === 'stripe') {
      requestPath = `${requestPath}?new_price_id=${newPriceId}`;
    }

    return get(requestPath);
  };

  const upgradeStripeSubscription = async (newPriceId: string) => {
    await put('/v1/web/stripe/subscriptions/upgrade', {
      newPriceId,
      introPriceId: process.env.NEXT_PUBLIC_STRIPE_INTRO_PRICE_ID,
    });
  };

  const upgradePaypalSubscription = async (newSubscriptionId: string) => {
    await put('/v1/web/paypal/subscriptions/upgrade', {
      newSubscriptionId,
    });
  };

  const cancelSubscription = async (subscriptionProvider: string) =>
    deleteRequest(`/v1/web/${subscriptionProvider}/subscriptions`);

  const cancelSubscriptionFromEmail = async (
    subscriptionProvider: string,
    cancellationCode: string
  ) =>
    deleteRequest(`/v1/web/${subscriptionProvider}/subscriptions/email`, {
      cancellationCode,
    });

  const getWithdrawalEligibility = async (
    subscriptionProvider: string
  ): Promise<WithdrawalEligibility> =>
    get(`/v1/web/${subscriptionProvider}/withdrawal/eligibility`);

  const exerciseWithdrawal = async (
    subscriptionProvider: string,
    confirmationEmail: string
  ): Promise<WithdrawalResult> =>
    post(`/v1/web/${subscriptionProvider}/withdrawal`, { confirmationEmail });

  const isPaypal = (providerRef: string): boolean =>
    providerRef === PaymentProvider.PayPal;

  const trackPayment = ({
    track,
    status,
    paymentPlatform,
    priceId,
    freeTrialEnabled,
    emailReminderEnabled,
    paymentStrategy,
    webOnboardingUserId,
    webOnboardingLocalStorageId,
    paywallVersion,
    previousAction,
    offerPlan,
  }: {
    track: (
      category: string,
      action: string,
      additionalParams?: Record<string, any> | undefined,
      label?: string | undefined
    ) => void;
    status: string;
    paymentPlatform?: PaymentPlatform;
    priceId?: string;
    freeTrialEnabled?: boolean;
    emailReminderEnabled?: boolean;
    paymentStrategy: 'subscription';
    webOnboardingUserId?: string;
    webOnboardingLocalStorageId?: string;
    paywallVersion?: string;
    previousAction?: string;
    offerPlan?: 'weekly' | 'yearly' | 'welcome' | 'free';
  }) => {
    track(
      'payment',
      status,
      {
        paymentPlatform,
        priceId,
        freeTrialEnabled,
        emailReminderEnabled,
        paymentStrategy,
        webOnboardingUserId,
        webOnboardingLocalStorageId,
        paywallVersion,
        ...(previousAction ? { previousAction } : {}),
        ...(offerPlan ? { offerPlan } : {}),
      },
      paymentPlatform
    );
  };

  const catchStripePriceError = (
    stripe?: Stripe | null,
    elements?: StripeElements | null,
    priceId?: string,
    ev?: any
  ) => {
    if (!stripe || !elements || !priceId) {
      Sentry.captureException(
        new Error('Unmet conditions. Wait a few seconds and try again.')
      );
      if (ev) {
        return ev.complete('fail');
      }
    }
  };

  const isPremiumUser = (user?: User): boolean =>
    !!user &&
    (user.monetization?.isSubscribed || user.giftCode?.isActive || user.isFree);

  const isBaseUser = (user?: User): boolean =>
    !user ||
    (!user.monetization?.isSubscribed &&
      !user.giftCode?.isActive &&
      !user.isFree);

  // TODO: understand why BASE/weekly has the same priceId of BUSINESS/weekly
  const getPaymentStrategiesWithoutBase = (
    paymentStrategies: PaymentStrategies
  ): [PaymentStrategiesIds, RecurringPayments][] => [
    [PaymentStrategiesIds.Personal, paymentStrategies.personal],
    [PaymentStrategiesIds.Business, paymentStrategies.business],
  ];

  const getUserPlanFromPriceId = (
    settingsPaymentStrategies: PaymentStrategies,
    userPriceId?: User['monetization']['priceId']
  ): PaymentStrategiesIds | '' => {
    const paymentStrategies = getPaymentStrategiesWithoutBase(
      settingsPaymentStrategies
    );

    return paymentStrategies.reduce((acc, strategy) => {
      const matchPrice = Object.values(strategy[1]).find(
        (period: { price: string; deprecatedPrices?: string[] }) =>
          period.price === userPriceId ||
          (period.deprecatedPrices || []).includes(userPriceId || '')
      );

      return matchPrice ? strategy[0] : acc;
    }, '');
  };

  const isBusinessUser = (user?: User): boolean =>
    !!user &&
    (user.paymentStrategy === PaymentStrategiesIds.Business ||
      user.giftCode?.isActive ||
      user.isFree ||
      user.monetization?.subscriptionProvider === 'mobile');

  const isPersonalUser = (user?: User): boolean =>
    user?.paymentStrategy === PaymentStrategiesIds.Personal;

  return {
    createPortalSession,
    isPersonalUser,
    isBusinessUser,
    getUserPlanFromPriceId,
    getPaymentStrategiesWithoutBase,
    isBaseUser,
    catchStripePriceError,
    isPremiumUser,
    trackPayment,
    isPaypal,
    cancelSubscription,
    upgradePaypalSubscription,
    cancelSubscriptionFromEmail,
    upgradeStripeSubscription,
    previewSubscriptionUpgrade,
    getSubscription,
    createPaypalSubscription,
    createStripeSubscription,
    getStripeClientIntentsData,
    formatRecurringInterval,
    useWithdrawalEligibility,
    getWithdrawalEligibility,
    exerciseWithdrawal,
  };
};

export default serviceSingletonGetter(
  Symbol('paymentService'),
  createPaymentService
);
